import React from 'react';
import { Badge } from '../../components/ui/badge';
import { formatDate, formatSalary, getLicenseBadgeClass } from '../../lib/utils';
import {
  MapPin,
  Clock,
  DollarSign,
  Building2,
  Briefcase,
  CheckCircle2,
  Globe,
  ExternalLink,
  Zap,
  Info,
} from 'lucide-react';

export const JobDetailMain = ({ job }) => {
  return (
    <div className="lg:col-span-2">
      <div className="bg-white rounded-xl p-6 sm:p-8 shadow-sm border border-gray-100">
        {/* Header */}
        <div className="flex items-start gap-4 mb-6">
          <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Building2 className="w-7 h-7 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-serif text-2xl sm:text-3xl font-bold text-gray-900 mb-1">
              {job.title}
            </h1>
            <p className="text-lg text-gray-600">{job.company_name}</p>
          </div>
        </div>

        {/* Badges */}
        <div className="flex flex-wrap gap-2 mb-6">
          <Badge className={`${getLicenseBadgeClass(job.license_required)} text-sm`}>
            {job.license_required} Required
          </Badge>
          <Badge variant="outline" className="text-sm">
            {job.job_type.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
          </Badge>
          {job.is_remote && (
            <Badge className="bg-purple-100 text-purple-700 text-sm">
              <Globe className="w-3 h-3 mr-1" />
              Remote
            </Badge>
          )}
          {(job.is_external || job.application_type === 'external') ? (
            <Badge className="bg-blue-100 text-blue-700 text-sm">
              <ExternalLink className="w-3 h-3 mr-1" />
              External Application
            </Badge>
          ) : (
            <Badge className="bg-green-100 text-green-700 text-sm">
              <Zap className="w-3 h-3 mr-1" />
              Easy Apply on INASW
            </Badge>
          )}
        </div>

        {/* External application notice */}
        {(job.is_external || job.application_type === 'external') && (
          <div className="mb-6 flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800" data-testid="external-application-notice">
            <Info className="w-5 h-5 flex-shrink-0 mt-0.5 text-blue-600" />
            <p className="leading-relaxed">
              This is an <strong>external listing</strong>{job.company_name ? ` from ${job.company_name}` : ''}. You'll create a free INASW account, then complete the application on the employer's official website. Creating an account or continuing to an external link does not confirm that an application has been submitted.
            </p>
          </div>
        )}

        {/* Meta */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg mb-8">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-700">{job.location}</span>
          </div>
          <div className="flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-700">{formatSalary(job.salary_min, job.salary_max)}</span>
          </div>
          <div className="flex items-center gap-2">
            <Briefcase className="w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-700">{job.application_count} applicants</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-700">{formatDate(job.created_at)}</span>
          </div>
        </div>

        {/* Description */}
        <div className="mb-8">
          <h2 className="font-serif text-xl font-semibold text-gray-900 mb-4">
            Job Description
          </h2>
          <div className="prose prose-gray max-w-none">
            <p className="text-gray-600 whitespace-pre-wrap">{job.description}</p>
          </div>
        </div>

        {/* Requirements */}
        {job.requirements && job.requirements.length > 0 && (
          <div className="mb-8">
            <h2 className="font-serif text-xl font-semibold text-gray-900 mb-4">
              Requirements
            </h2>
            <ul className="space-y-2">
              {job.requirements.map((req, index) => (
                <li key={index} className="flex items-start gap-3 text-gray-600">
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span>{req}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Benefits */}
        {job.benefits && job.benefits.length > 0 && (
          <div>
            <h2 className="font-serif text-xl font-semibold text-gray-900 mb-4">
              Benefits
            </h2>
            <ul className="space-y-2">
              {job.benefits.map((benefit, index) => (
                <li key={index} className="flex items-start gap-3 text-gray-600">
                  <CheckCircle2 className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Technology & Administrative Support (employer-reported) */}
        <div data-testid="job-tech-admin">
          <h2 className="font-serif text-xl font-semibold text-gray-900 mb-3">
            Technology &amp; Administrative Support
          </h2>
          {(() => {
            const items = [...(job.tech_admin_support || [])];
            if (job.tech_admin_support_other) items.push(`Other: ${job.tech_admin_support_other}`);
            const prominent = job.is_featured || job.is_featured_direct;
            if (items.length === 0) {
              return <p className="text-sm text-gray-500 italic" data-testid="tech-admin-unspecified">Not specified by employer.</p>;
            }
            return (
              <div className="flex flex-wrap gap-2">
                {items.map((it, i) => (
                  <Badge
                    key={i}
                    className={`${prominent ? 'bg-secondary text-white' : 'bg-secondary/10 text-secondary'} text-sm`}
                    data-testid={`tech-admin-badge-${i}`}
                  >
                    {it}
                  </Badge>
                ))}
              </div>
            );
          })()}
          <p className="text-xs text-gray-400 mt-3 italic">Employer-reported information—candidates should confirm details during the hiring process.</p>
        </div>
      </div>
    </div>
  );
};
